import React from 'react';
import { TrendingUp, Repeat, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdminTopPerformers } from '@/hooks/admin/useAdminTopPerformers';

const formatPct = (v) => `${Math.round((v || 0) * 1000) / 10}%`;

/**
 * Surfaces the best-performing broadcasts so admins can double down on
 * what's working. Each row offers a one-tap "Promote weekly" action that
 * populates the broadcast form with the same content + a Weekly recurrence
 * pre-set to today's weekday at 09:00 UTC. Admin tweaks + saves.
 */
const TopPerformersCard = ({ enabled = true, onPromote, refreshKey = 0 }) => {
  const { items, loading, refresh } = useAdminTopPerformers({ enabled });

  // Pull-to-refresh whenever the parent signals (e.g. after a new broadcast).
  React.useEffect(() => {
    if (refreshKey > 0) refresh();
  }, [refreshKey, refresh]);

  return (
    <div
      className="p-4 sm:p-6 rounded-2xl bg-card border border-border/50 space-y-4"
      data-testid="top-performers-card"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <TrendingUp className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold leading-tight">Top Performers</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Sorted by click-through rate — promote winners to a weekly recurrence with one tap.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={loading}
          data-testid="top-performers-refresh-btn"
        >
          {loading ? '…' : 'Refresh'}
        </Button>
      </div>

      {items.length === 0 ? (
        <div
          className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border/50 rounded-xl"
          data-testid="top-performers-empty"
        >
          No sent broadcasts yet — your top performers will appear here once recipients open them.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="top-performers-list">
          {items.map((b, idx) => (
            <li
              key={b.broadcast_id}
              className="p-3 rounded-xl bg-muted/30 border border-border/50 flex items-start gap-3"
              data-testid={`top-performer-row-${b.broadcast_id}`}
            >
              <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                {idx + 1}
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <p className="font-medium text-sm truncate">{b.title}</p>
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                  <span>
                    CTR <span
                      className="text-foreground font-medium"
                      data-testid={`top-performer-ctr-${b.broadcast_id}`}
                    >{formatPct(b.ctr)}</span>
                  </span>
                  <span>
                    open rate <span className="text-foreground font-medium">{formatPct(b.open_rate)}</span>
                  </span>
                  <span>
                    audience <span className="text-foreground font-medium">{b.audience_size}</span>
                  </span>
                  <span>opens <span className="text-foreground font-medium">{b.opens_count}</span></span>
                  <span>clicks <span className="text-foreground font-medium">{b.clicks_count}</span></span>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onPromote?.(b)}
                data-testid={`top-performer-promote-${b.broadcast_id}`}
                className="shrink-0"
              >
                <Repeat className="h-3.5 w-3.5 mr-1" />
                <Sparkles className="h-3.5 w-3.5 mr-1" />
                Promote weekly
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TopPerformersCard;
